import { ArrowButton } from "@/components/mypage/arrowButton";
import { useAuthStore } from "@/store/auth.store";
import { pointsToYenForCalculations } from "@/utils/pointsToYen";
import { ThumbaTrophyIcon } from "@roman/shared/components/Icons";
import type React from "react";
import { useState } from "react";
import type { CardQuantity, MethodContent } from "..";
import { calculateTotalYenWithCommission } from "../utils";
import { PriceSelectionCard } from "./PriceSelectionCard";

interface PaymentFormProps {
  methodContent: MethodContent | null;
  onClick: () => void;
  cardQuantities: CardQuantity[];
  setCardQuantities: React.Dispatch<React.SetStateAction<CardQuantity[]>>;
  backButtonClick: () => void;
}

export const PaymentForm: React.FC<PaymentFormProps> = ({
  methodContent,
  onClick,
  cardQuantities,
  setCardQuantities,
  backButtonClick,
}) => {
  const user = useAuthStore((state) => state.user);
  if (!user) {
    throw new Error("User not found");
  }
  const MAXIMUM_WITHDRAWAL = Number(user.redeemablePoints) / 10 < 100000 ? Number(user?.redeemablePoints) / 10 : 100000;
  const [totalYen, setTotalYen] = useState<number>(() => calculateTotalYenWithCommission(cardQuantities));

  const handleQuantityChange = (yenOption: string, quantity: number) => {
    const newQuantities =
      quantity === 0
        ? cardQuantities.filter((item) => item.yenOption !== yenOption)
        : [...cardQuantities.filter((item) => item.yenOption !== yenOption), { yenOption, quantity }];

    const newTotal = calculateTotalYenWithCommission(newQuantities);
    setCardQuantities(newQuantities);
    setTotalYen(newTotal);
  };

  if (!methodContent) {
    return;
  }

  return (
    <div className="bg-white min-w-full min-h-screen">
      <form className="p-[18px] flex flex-col items-start gap-10">
        <div className="flex flex-col items-start gap-3 w-full">
          <div className="flex items-center gap-3">
            <ArrowButton className="-rotate-90" onClick={backButtonClick} />
            <p className="text-xl font-bold -tracking-[.01em] text-black">{methodContent.heading}</p>
          </div>
          <div className="flex flex-col items-start gap-1">
            <p className="text-[22px] text-primary font-bold -tracking-[.01em]">{methodContent.exchangeRange}</p>
            <p className="text-[10px] font-normal -tracking-[.01em] text-gray">{methodContent.description}</p>
          </div>
        </div>

        <div className="flex flex-col items-start gap-3 w-full">
          <div className="flex items-center">
            <p className="text-xl font-bold -tracking-[.01em] text-black">交換額を選定</p>
          </div>

          <div className="grid grid-cols-2 gap-3 w-full">
            {methodContent.availableYenOptions.map((yen) => (
              <PriceSelectionCard
                key={`price-${yen}`}
                yen={yen}
                totalYen={totalYen}
                onYenChange={(yen, quantity) => handleQuantityChange(yen.toString(), quantity)}
                maxYen={MAXIMUM_WITHDRAWAL}
                initialQuantity={cardQuantities.find((item) => item.yenOption === yen)?.quantity || 0}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col items-start gap-3 w-full">
          <div className="flex items-center">
            <p className="text-xl font-bold -tracking-[.01em] text-black">メールアドレス</p>
          </div>
          <div className="flex flex-col items-start gap-3 w-full">
            <p className="text-base w-full font-normal h-[19px] pb-[2rem] border-b border-disabled border-solid outline-none">
              {user.googleEmail}
            </p>
          </div>
          <div>
            <p className="text-primary text-xs font-medium">上記のメールアドレスにギフトが送信されます</p>
          </div>
        </div>
        <div className="flex flex-col items-start gap-3 w-full">
          <div className="flex items-center">
            <p className="text-xl font-bold -tracking-[.01em] text-black">商品詳細</p>
          </div>
          <div>
            <p className="text-xs font-gray">
              詳細は
              <a href="#todo" className="text-primary">
                利用規約
              </a>
              を確認
            </p>
          </div>
        </div>

        <div className="flex flex-col items-start gap-3 w-full">
          <div className="flex items-center">
            <p className="text-xl font-bold -tracking-[.01em] text-black">合計消費円分</p>
          </div>
          <div className="p-1 bg-[#EFF9FF] flex items-center justify-end w-full">
            <div className="flex items-center gap-2.5">
              <div className="h-6 w-6 flex items-center justify-center">
                <ThumbaTrophyIcon />
              </div>
              <p className="text-primary text-xl font-bold">{totalYen > 0 ? `${totalYen.toLocaleString()}` : "-"}</p>
            </div>
          </div>
          <div className="flex items-center justify-end w-full gap-3">
            <p className="text-gray text-xs font-medium">交換後のポイント残高</p>
            <div className="flex items-center gap-1">
              <div className="h-4 w-4 flex items-center justify-center">
                <ThumbaTrophyIcon />
              </div>
              <p className="text-gray text-xs font-medium">
                {totalYen > 0
                  ? `${(pointsToYenForCalculations(user?.redeemablePoints || "0") - totalYen).toLocaleString()} 円分`
                  : "-"}
              </p>
            </div>
          </div>
        </div>

        <button
          className={`flex items-center justify-center text-white py-2.5 px-4 text-lg font-bold w-full rounded-md ${
            totalYen === 0 ? "bg-muted" : "bg-primary"
          }`}
          type="button"
          onClick={onClick}
          disabled={totalYen === 0}
        >
          交換する
        </button>
      </form>
    </div>
  );
};
